/**
 * CitationCard - Individual Literature Citation Display
 * ============================================================================
 * DC-1006: Displays a single literature citation with metadata and relevance.
 *
 * Features:
 * - Title linked to PubMed
 * - Authors, journal, year display
 * - PMID/DOI badges
 * - Relevance score bar with color coding
 * - Collapsible key quote blockquote
 * - Full text availability badge
 *
 * @version 1.0.0
 * @created 2026-01-24
 * ============================================================================
 */

import React, { useState } from 'react';
import type { LiteratureCitationRef } from '../../types/bridgeIntelligence';
import './citation.css';

// ============================================================================
// Types
// ============================================================================

export interface CitationCardProps {
  /** The citation to display */
  citation: LiteratureCitationRef;
  /** Compact mode for smaller layouts */
  compact?: boolean;
  /** Custom className */
  className?: string;
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Get color class based on relevance score
 * - Green: >80 (high relevance)
 * - Yellow: 60-80 (moderate relevance)
 * - Gray: <60 (lower relevance)
 */
const getRelevanceColorClass = (score: number): string => {
  if (score >= 80) return 'relevance-high';
  if (score >= 60) return 'relevance-moderate';
  return 'relevance-low';
};

/**
 * Format PubMed URL from PMID
 */
const getPubMedUrl = (pmid: string): string => {
  return `https://pubmed.ncbi.nlm.nih.gov/${pmid}`;
};

/**
 * Format DOI URL
 */
const getDoiUrl = (doi: string): string => {
  return `https://doi.org/${doi}`;
};

// ============================================================================
// Main Component
// ============================================================================

export const CitationCard: React.FC<CitationCardProps> = ({
  citation,
  compact = false,
  className = '',
}) => {
  const [showQuote, setShowQuote] = useState(false);

  const relevanceClass = getRelevanceColorClass(citation.relevance_score);

  return (
    <div className={`citation-card ${compact ? 'citation-card--compact' : ''} ${className}`}>
      {/* Title Row */}
      <div className="citation-card__header">
        <a
          href={getPubMedUrl(citation.pmid)}
          target="_blank"
          rel="noopener noreferrer"
          className="citation-card__title"
          title="View on PubMed"
        >
          {citation.title}
        </a>
        {citation.full_text_available && (
          <span className="citation-card__fulltext-badge" title="Full text available in PubMed Central">
            Full Text
          </span>
        )}
      </div>

      {/* Authors & Journal */}
      <div className="citation-card__meta">
        <span className="citation-card__authors">{citation.authors}</span>
        {citation.journal && (
          <>
            <span className="citation-card__separator">|</span>
            <span className="citation-card__journal">{citation.journal}</span>
          </>
        )}
        {citation.year && (
          <>
            <span className="citation-card__separator">|</span>
            <span className="citation-card__year">{citation.year}</span>
          </>
        )}
      </div>

      {/* ID Badges */}
      <div className="citation-card__badges">
        <span className="citation-card__badge citation-card__badge--pmid">
          PMID: {citation.pmid}
        </span>
        {citation.doi && (
          <a
            href={getDoiUrl(citation.doi)}
            target="_blank"
            rel="noopener noreferrer"
            className="citation-card__badge citation-card__badge--doi"
            title="View via DOI"
          >
            DOI: {citation.doi}
          </a>
        )}
      </div>

      {/* Relevance Score Bar */}
      <div className="citation-card__relevance">
        <div className="citation-card__relevance-header">
          <span className="citation-card__relevance-label">Relevance</span>
          <span className={`citation-card__relevance-score ${relevanceClass}`}>
            {citation.relevance_score}%
          </span>
        </div>
        <div className="citation-card__relevance-bar-container">
          <div
            className={`citation-card__relevance-bar ${relevanceClass}`}
            style={{ width: `${citation.relevance_score}%` }}
          />
        </div>
        {!compact && citation.relevance_explanation && (
          <p className="citation-card__relevance-explanation">
            {citation.relevance_explanation}
          </p>
        )}
      </div>

      {/* Key Quote (Collapsible) */}
      {citation.key_quote && !compact && (
        <div className="citation-card__quote-section">
          <button
            className="citation-card__quote-toggle"
            onClick={() => setShowQuote(!showQuote)}
            aria-expanded={showQuote}
          >
            {showQuote ? 'Hide Key Quote' : 'Show Key Quote'}
            <span className="citation-card__quote-icon">{showQuote ? '▲' : '▼'}</span>
          </button>
          {showQuote && (
            <blockquote className="citation-card__quote">
              "{citation.key_quote}"
            </blockquote>
          )}
        </div>
      )}
    </div>
  );
};

export default CitationCard;
